import {
  type WebSession,
  type WebSessionCourseListItem,
  WebSessionCourseListName,
} from '@/models/WebSession';
import { LoginOrigin } from '@/models/User';

import { ref, toRaw } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import { useLocalStorage, useSessionStorage } from '@/composables/useStorage';

import { getToken, parseJwt } from '@/utils/storage';
import { getWebsessionEndpoint, getWebsessionWithIdEndpoint } from '@/utils/endpoints';
import { validateSsoReferrer } from '@/utils/validateReferrer';
import fetch, { handleUnauthorizedRequest } from '@/utils/fetch';

import { useSessionStore } from './useSessionStore';

export const useWebSessionStore = defineStore('webSession', () => {
  const isLoading = ref<boolean>(false);
  const webSession = ref<WebSession>();

  const { get: readSessionWebSession, set: saveSessionWebSession } =
    useSessionStorage('websession');
  const { get: readLocalWebSession, set: saveLocalWebSession } = useLocalStorage('websession');

  const sessionStore = useSessionStore();
  const { isLms } = storeToRefs(sessionStore);
  const { setPlatform } = sessionStore;

  const initialize = async () => {
    if (webSession.value) return;

    const token = getToken();
    if (!token) {
      console.error('No token detected.');
      return;
    }

    const parsedToken = parseJwt(token);
    if (!parsedToken) {
      console.error('Invalid token detected.');
      return;
    }

    setPlatform(parsedToken.LoginOrigin);

    const isSsoOrigin =
      parsedToken.LoginOrigin === LoginOrigin.SSO &&
      validateSsoReferrer(document.referrer, window.location.origin);

    const savedWebSession = isLms.value ? readSessionWebSession() : readLocalWebSession();

    if (isSsoOrigin || !savedWebSession || savedWebSession.CurrentUser?.Id < 0) {
      await fetchWebSession();
      return;
    }

    setWebSession(savedWebSession);
  };

  const fetchWebSession = async () => {
    if (isLoading.value) return;

    isLoading.value = true;

    try {
      const endpoint = isLms.value ? getWebsessionWithIdEndpoint() : getWebsessionEndpoint();
      const response = await fetch(endpoint);
      const newWebSession: WebSession = await response.json();

      setWebSession(newWebSession);
    } catch {
      console.error('Failed to fetch web session!');
      handleUnauthorizedRequest();
    } finally {
      isLoading.value = false;
    }
  };

  const setWebSession = (newWebSession: WebSession | undefined) => {
    if (!newWebSession) return;

    webSession.value = newWebSession;

    if (isLms.value) {
      saveSessionWebSession(newWebSession);
    } else {
      saveLocalWebSession(newWebSession);
    }
  };

  const updateCourseList = (
    courseId: number,
    listName: WebSessionCourseListName,
    action: (list: WebSessionCourseListItem) => void,
  ) => {
    if (!webSession.value || !courseId || isLms.value) return;

    const clonedWebSession: WebSession = JSON.parse(JSON.stringify(toRaw(webSession.value)));
    const courseList = clonedWebSession.CourseListOrder.find(
      (listItem) => listItem.Name === listName,
    );
    if (!courseList) return;

    action(courseList);

    setWebSession(clonedWebSession);
  };

  const updateWebSessionFavoritedCoursesList = (courseId: number, favoritedStatus: boolean) => {
    updateCourseList(courseId, WebSessionCourseListName.Fav, (favoritedCoursesList) => {
      if (favoritedStatus) {
        favoritedCoursesList.Course.unshift(courseId);
      } else {
        favoritedCoursesList.Course.splice(favoritedCoursesList.Course.indexOf(courseId), 1);
      }
    });
  };

  const updateWebsessionRunningCoursesList = (
    courseId: number,
    courseShouldBeRemovedFromList = false,
  ) => {
    updateCourseList(courseId, WebSessionCourseListName.Running, (runningCoursesList) => {
      const courseIndex = runningCoursesList.Course.indexOf(courseId);
      if (courseIndex > -1) {
        runningCoursesList.Course.splice(courseIndex, 1);
      }
      if (!courseShouldBeRemovedFromList) {
        runningCoursesList.Course.unshift(courseId);
      }
    });
  };

  return {
    webSession,
    initialize,
    updateWebSessionFavoritedCoursesList,
    updateWebsessionRunningCoursesList,
  };
});
